import { type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PageSkeleton from '../../components/common/page-skeleton';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { connectionTypesService } from '../../services/connection-types.service';
import ScreenHeader from '../../components/common/screen-header';

const LIST = '/partners?tab=types';

const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs font-medium text-ink-muted uppercase tracking-wide">{label}</span>
    <span className="text-sm text-ink">{value ?? '-'}</span>
  </div>
);

const ConnectionTypeView = () => {
  const navigate = useAppNavigate();
  const { id } = useParams<{ id: string }>();
  const canUpdate = useCanUpdate(JOB_NAMES.CONNECTION_TYPES);

  const { data: row, isLoading } = useQuery({
    queryKey: ['connection-types', id],
    queryFn: () => connectionTypesService.getById(id!),
    enabled: !!id,
  });

  useBreadcrumbTitle(row?.name || null);

  if (isLoading) return <PageSkeleton />;
  if (!row) return null;

  return (
    <PermissionGuard jobName={JOB_NAMES.CONNECTION_TYPES}>
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-ConnectionType-View">
        <ScreenHeader
          icon="pi pi-link"
          title={row.name || 'Connection Type'}
          subtitle={row.code}
          onBack={() => navigate(LIST)}
          right={canUpdate && (
            <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => navigate(`/partners/connection-types/${id}/edit`)} />
          )}
        />

        <div className="grid grid-cols-1 gap-3">
          <div className="ds-card p-4">
            <h2 className="text-sm font-semibold text-ink mb-3">Connection Type</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Code" value={<span className="font-mono">{row.code}</span>} />
              <Field label="Name" value={row.name || '-'} />
              <Field label="Supported Auth Types" value={(row.supportedAuthTypes || []).join(', ') || '-'} />
              <Field label="Active" value={row.isActive ? 'Yes' : 'No'} />
              <div className="sm:col-span-2">
                <Field label="Description" value={row.description || '-'} />
              </div>
            </div>
          </div>

          {row.credentialSchema && (
            <div className="ds-card p-4">
              <h2 className="text-sm font-semibold text-ink mb-3">Credential Schema</h2>
              <pre className="bg-canvas dark:bg-gray-900 rounded p-3 text-xs overflow-auto max-h-96 font-mono">
                {JSON.stringify(row.credentialSchema, null, 2)}
              </pre>
            </div>
          )}

          <AuditInfoCard
            createdAt={(row as any).createdAt}
            updatedAt={(row as any).updatedAt}
            createdBy={(row as any).createdBy}
            updatedBy={(row as any).updatedBy}
          />
        </div>
      </div>
    </PermissionGuard>
  );
};

export default ConnectionTypeView;
